/**
 * AmbientEffects - Background ambient effects for atmosphere
 *
 * Features:
 * - Floating dust particles
 * - CRT scan lines effect
 * - Occasional screen flicker
 * - Vignette overlay
 * - Respects reduced motion preferences
 */

import React, { useEffect, useState, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Check for reduced motion preference
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Generate random number in range
const random = (min, max) => Math.random() * (max - min) + min;

/**
 * Floating Dust Particles
 */
const DustParticle = memo(({ id, size, startX, startY, duration, delay }) => (
  <motion.div
    className="absolute rounded-full bg-white/20 pointer-events-none"
    style={{
      width: size,
      height: size,
      left: startX,
      top: startY,
      filter: 'blur(0.5px)',
    }}
    initial={{ opacity: 0, y: 0 }}
    animate={{
      opacity: [0, 0.6, 0.4, 0.6, 0],
      y: [-20, -100 - random(0, 50)],
      x: [0, random(-30, 30), random(-20, 20)],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'linear',
    }}
  />
));

DustParticle.displayName = 'DustParticle';

const DustParticles = ({ count = 30, enabled = true }) => {
  const particles = useMemo(() => {
    if (!enabled) return [];
    return Array.from({ length: count }, (_, i) => ({
      id: `dust-${i}`,
      size: random(1, 3),
      startX: `${random(0, 100)}%`,
      startY: `${random(80, 120)}%`,
      duration: random(8, 15),
      delay: random(0, 10),
    }));
  }, [count, enabled]);

  if (!enabled) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {particles.map((particle) => (
        <DustParticle key={particle.id} {...particle} />
      ))}
    </div>
  );
};

/**
 * CRT Scan Lines Effect
 */
const ScanLines = ({ opacity = 0.03, enabled = true }) => {
  if (!enabled) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[2]"
      style={{
        backgroundImage: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0, 0, 0, ${opacity}) 2px,
          rgba(0, 0, 0, ${opacity}) 4px
        )`,
        backgroundSize: '100% 4px',
      }}
    />
  );
};

/**
 * Moving Scan Line (like old CRT monitors)
 */
const MovingScanLine = ({ enabled = true, speed = 8 }) => {
  if (!enabled) return null;

  return (
    <motion.div
      className="fixed left-0 right-0 h-[2px] pointer-events-none z-[2]"
      style={{
        background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.03), transparent)',
        boxShadow: '0 0 10px rgba(255,255,255,0.05)',
      }}
      initial={{ top: '-2px' }}
      animate={{ top: '100vh' }}
      transition={{
        duration: speed,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  );
};

/**
 * Screen Flicker Effect
 */
const ScreenFlicker = ({ enabled = true, intensity = 0.02 }) => {
  const [isFlickering, setIsFlickering] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Random flicker intervals
    const scheduleFlicker = () => {
      const nextFlicker = random(5000, 15000); // 5-15 seconds
      return setTimeout(() => {
        setIsFlickering(true);
        setTimeout(() => setIsFlickering(false), random(50, 150));
        scheduleFlicker();
      }, nextFlicker);
    };

    const timer = scheduleFlicker();
    return () => clearTimeout(timer);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <AnimatePresence>
      {isFlickering && (
        <motion.div
          className="fixed inset-0 pointer-events-none z-[3]"
          style={{ backgroundColor: `rgba(255,255,255,${intensity})` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0, 0.5, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        />
      )}
    </AnimatePresence>
  );
};

/**
 * Vignette Overlay
 */
const Vignette = ({ intensity = 0.4, enabled = true }) => {
  if (!enabled) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[1]"
      style={{
        background: `radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,${intensity}) 100%)`,
      }}
    />
  );
};

/**
 * Noise/Static Overlay
 */
const NoiseOverlay = ({ opacity = 0.02, enabled = true }) => {
  const [noiseKey, setNoiseKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    // Animate noise by changing key periodically
    const interval = setInterval(() => {
      setNoiseKey((prev) => prev + 1);
    }, 100);

    return () => clearInterval(interval);
  }, [enabled]);

  // Generate SVG noise pattern (hooks must precede the early return below)
  const noiseUrl = useMemo(() => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
        <filter id="noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="${noiseKey}" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noise)" opacity="${opacity}" />
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }, [noiseKey, opacity]);

  if (!enabled) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[1] mix-blend-overlay"
      style={{
        backgroundImage: `url("${noiseUrl}")`,
        backgroundRepeat: 'repeat',
      }}
    />
  );
};

/**
 * Glow Pulse Effect (for corners/edges)
 */
const GlowPulse = ({ color = 'rgba(52, 152, 219, 0.1)', enabled = true }) => {
  if (!enabled) return null;

  return (
    <>
      {/* Top left corner glow */}
      <motion.div
        className="fixed top-0 left-0 w-64 h-64 pointer-events-none z-[1]"
        style={{
          background: `radial-gradient(circle at top left, ${color}, transparent 70%)`,
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      {/* Bottom right corner glow */}
      <motion.div
        className="fixed bottom-0 right-0 w-64 h-64 pointer-events-none z-[1]"
        style={{
          background: `radial-gradient(circle at bottom right, ${color}, transparent 70%)`,
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
    </>
  );
};

/**
 * Main Ambient Effects Component
 *
 * The full-screen fixed layers (L7) keep compositing even when the tab is
 * hidden, so the whole stack unmounts while document.hidden and remounts on
 * return — a paused tab should cost nothing.
 */
const AmbientEffects = ({
  enabled = true,
  dustParticles = true,
  dustCount = 25,
  scanLines = true,
  scanLineOpacity = 0.03,
  movingScanLine = true,
  screenFlicker = true,
  flickerIntensity = 0.02,
  vignette = true,
  vignetteIntensity = 0.3,
  noise = false, // Disabled by default as it can be heavy
  noiseOpacity = 0.015,
  glowPulse = true,
  glowColor = 'rgba(52, 152, 219, 0.08)',
}) => {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isHidden, setIsHidden] = useState(document.hidden);

  useEffect(() => {
    setReducedMotion(prefersReducedMotion());

    // Listen for changes to reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Pause all ambient layers while the tab is hidden (L7)
  useEffect(() => {
    const onVisibility = () => setIsHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Disable all effects if user prefers reduced motion, effects are disabled,
  // or the tab is in the background
  if (!enabled || reducedMotion || isHidden) return null;

  return (
    <>
      <DustParticles count={dustCount} enabled={dustParticles} />
      <ScanLines opacity={scanLineOpacity} enabled={scanLines} />
      <MovingScanLine enabled={movingScanLine} />
      <ScreenFlicker enabled={screenFlicker} intensity={flickerIntensity} />
      <Vignette intensity={vignetteIntensity} enabled={vignette} />
      <NoiseOverlay opacity={noiseOpacity} enabled={noise} />
      <GlowPulse color={glowColor} enabled={glowPulse} />
    </>
  );
};

// Export individual effects for custom compositions
export {
  DustParticles,
  ScanLines,
  MovingScanLine,
  ScreenFlicker,
  Vignette,
  NoiseOverlay,
  GlowPulse,
};

export default AmbientEffects;
